/**
 * Blog workspace — the single topic-centric page (route /blog/topic/:id).
 *
 * Doctrine (docs/blog-engine-konzept.md) + the user's directive: NO separate
 * "generate" interstitial. One page. The article is written and lives HERE:
 *   - LEFT  : the article — streams live while writing, renders when present,
 *             is editable in place (human rewrite), or an empty state before it
 *             exists. A comfortable measure, never cramped.
 *   - RIGHT : the control center ("Schaltzentrale") ABOVE the evidence core —
 *             Generate/Regenerate, Generate images, Hero image, Edit, Copy; the
 *             pipeline stages; the certificates. Then the evidence core (all
 *             caveat-free rubric claims, strongest first — the writer's ground).
 *
 * Scrolling: ONE page scroll. `relative` on the container is load-bearing (the
 * Markdown renderer emits a position:absolute sr-only heading that would
 * otherwise escape <main>'s overflow and add a phantom second scroll frame —
 * see infra_markdown_abspos_sronly_escapes_main_overflow). The right rail is
 * sticky and scrolls on its own only when it outgrows the viewport.
 *
 * English-only chrome (Atlas/blog doctrine — feedback_atlas_de_en_first).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Loader2,
  Sparkles,
  PenLine,
  Pencil,
  AlertTriangle,
  FileText,
  Image as ImageIcon,
  Wand2,
  Copy,
  Check,
  ShieldAlert,
  Square,
  Save,
  X,
  TrendingUp,
} from "lucide-react";
import { api, ApiError } from "../api";
import { EmptyState, useToast } from "../ui";
import Md from "../components/Md";
import { streamBlogGenerate } from "../blogStream";
import { HeroStudioModal, ImageEditPanel } from "./blog/imageStudio";
import SeoPanel from "./blog/SeoPanel";
import { bodyWithoutHero } from "./blog/heroBody";
import {
  StatusPill,
  TopicClassBadge,
  Eyebrow,
  EvidenceCard,
  CertChips,
  PipelineStepper,
  TrendMatchBadge,
  MoACard,
  BlogBackLink,
  type EviClaimLike,
  type CertChip,
  type MoACardData,
} from "./blog/shared";

interface TopicDetail {
  id: string;
  benefit_area: string;
  benefit_area_en?: string | null;
  search_intent: string;
  topic_class: string;
  status: string;
  format_hint: string | null;
  language: string;
  title: string | null;
  slug: string | null;
  article_id: string | null;
  trend_signal_id: string | null;
  blocked_reason: string | null;
  priority: number;
  notes: string | null;
  evidence: EviClaimLike[];
  cert_chips: CertChip[];
  moa_cards: MoACardData[];
}

interface ArticleImage {
  id: string;
  slot_type: string;
  position: number;
  anchor: string | null;
  alt: string | null;
  kind: string | null;
  status: string;
  url: string | null;
}

interface ArticleDetail {
  id: string;
  pillar: string;
  axis: string | null;
  fmt: string;
  language: string;
  topic: string;
  title: string | null;
  status: string;
  body_md: string;
  references: string[];
  claims_count: number;
  story_count: number;
  evidence_used: number;
  evidence_total: number;
  governance_required: boolean;
  governance_ok: boolean;
  model: string | null;
  cost_eur: number | null;
  hero_image_url: string | null;
  images: ArticleImage[];
  evidence?: EviClaimLike[];
  benefit_area_en?: string | null;
  topic_id?: string | null;
  // Output fields (P1-AUSGABE) — shown/edited in the SEO panel.
  subtitle?: string | null;
  slug?: string | null;
  meta_description?: string | null;
  primary_keyword?: string | null;
  author_name?: string | null;
  reviewed_by?: string | null;
  cta_variant?: string | null;
  lint?: { numbers?: { unsupported?: string[] }; anchors?: { unresolved?: string[]; key_mismatch?: string[] } } | null;
}

interface TrendContext {
  id: string;
  term: string;
  headline: string | null;
  framing_md: string | null;
  claim_match: string | null;
}

export default function BlogTopicDetailPage() {
  const { id = "" } = useParams();
  const toast = useToast();

  const [topic, setTopic] = useState<TopicDetail | null>(null);
  const [article, setArticle] = useState<ArticleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Kür-Topics: der Trend, aus dem sie promotet wurden (Kontext + Writer-Futter).
  const [trend, setTrend] = useState<TrendContext | null>(null);

  // Generation (streaming) state.
  const [generating, setGenerating] = useState(false);
  const [streamText, setStreamText] = useState("");
  const cancelRef = useRef<null | (() => void)>(null);

  // In-place edit state.
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState("");
  const [saving, setSaving] = useState(false);

  // Secondary busy flags + the image edit panel target.
  const [imgBusy, setImgBusy] = useState(false);
  const [heroOpen, setHeroOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [panelImage, setPanelImage] = useState<{ url: string; slot_type?: string } | null>(null);

  const loadArticle = useCallback(async (articleId: string) => {
    try {
      const a = await api.get<ArticleDetail>(`/api/blog/v2/articles/${articleId}`);
      setArticle(a);
    } catch (e) {
      console.error("load article failed", e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const t = await api.get<TopicDetail>(`/api/blog-dashboard/topic/${id}`);
      setTopic(t);
      if (t.article_id) await loadArticle(t.article_id);
      else setArticle(null);
      // Kür-Topic → den Quell-Trend nachladen (framing = Writer-Kontext).
      if (t.trend_signal_id) {
        try {
          setTrend(await api.get<TrendContext>(`/api/blog-dashboard/trends/${t.trend_signal_id}`));
        } catch {
          setTrend(null);
        }
      } else {
        setTrend(null);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setNotFound(true);
      else console.error("load topic failed", e);
    } finally {
      setLoading(false);
    }
  }, [id, loadArticle]);

  useEffect(() => {
    load();
    return () => cancelRef.current?.();
  }, [load]);

  const noEvi = (topic?.evidence.length ?? 0) === 0;

  // ── Generate / regenerate (streaming) ──────────────────────────────────────
  const generate = () => {
    if (!topic || generating) return;
    setGenerating(true);
    setStreamText("");
    setEditing(false);
    setPanelImage(null);
    const { cancel } = streamBlogGenerate(
      { topic_id: topic.id, fmt: "blog", use_story: null },
      {
        onDelta: (t) => setStreamText((cur) => cur + t),
        onDone: async () => {
          cancelRef.current = null;
          setGenerating(false);
          setStreamText("");
          await load(); // pulls the persisted (scrubbed) article + fresh status
          toast.ok("Article generated.");
        },
        onError: (err) => {
          cancelRef.current = null;
          setGenerating(false);
          setStreamText("");
          toast.err("Generation failed", err.message);
        },
      },
    );
    cancelRef.current = cancel;
  };

  const stopGenerate = () => {
    cancelRef.current?.();
    cancelRef.current = null;
    setGenerating(false);
    setStreamText("");
  };

  // ── Edit (human rewrite) ───────────────────────────────────────────────────
  const startEdit = () => {
    if (!article) return;
    setEditBody(article.body_md);
    setPanelImage(null);
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!article) return;
    setSaving(true);
    try {
      const a = await api.patch<ArticleDetail>(`/api/blog/v2/articles/${article.id}`, {
        body_md: editBody,
      });
      setArticle(a);
      setEditing(false);
      toast.ok("Saved.");
    } catch (e) {
      toast.err("Save failed", String(e));
    } finally {
      setSaving(false);
    }
  };

  // ── Image passes ───────────────────────────────────────────────────────────
  const addImages = async () => {
    if (!article) return;
    setImgBusy(true);
    try {
      const a = await api.post<ArticleDetail>(`/api/blog/v2/articles/${article.id}/images`);
      setArticle(a);
      toast.ok("Images generated and inserted.");
    } catch (e) {
      toast.err("Image pass failed", String(e));
    } finally {
      setImgBusy(false);
    }
  };

  const copyMarkdown = async () => {
    if (!article) return;
    try {
      await navigator.clipboard.writeText(article.body_md);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.err("Copy failed");
    }
  };

  // Image click routing (dataviz charts stay static).
  const isImageClickable = (url: string): boolean => {
    const im = article?.images.find((i) => i.url === url);
    if (im && /dataviz|chart/i.test(im.slot_type)) return false;
    return true;
  };
  const openPanel = (url: string) => {
    if (!isImageClickable(url)) return;
    const im = article?.images.find((i) => i.url === url);
    setPanelImage({ url, slot_type: im?.slot_type });
  };

  if (loading && !topic) {
    return (
      <div className="mx-auto max-w-4xl p-6 flex items-center gap-2 text-text-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading …
      </div>
    );
  }

  if (notFound || !topic) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <BackLink />
        <div className="mt-6">
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title="Topic not found"
            hint="It may have been removed. Head back to the dashboard."
          />
        </div>
      </div>
    );
  }

  const areaLabel = topic.benefit_area_en || topic.benefit_area;
  const hasArticle = !!article;
  const streaming = generating && streamText.length > 0;

  return (
    // `relative` = containing block for the Md renderer's abspos sr-only heading
    // → keeps the page to a SINGLE scroll (see file header + memory).
    <div className="relative max-w-[1200px] mx-auto px-4 md:px-6 py-6 space-y-4">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <BackLink />
        <span className="text-text-3/50">/</span>
        <span className="inline-flex items-center gap-1.5 text-text-3">
          <Eyebrow>{areaLabel}</Eyebrow>
          <TopicClassBadge topicClass={topic.topic_class} />
          {topic.trend_signal_id && (
            <span className="text-[0.66rem] uppercase tracking-wider">· from trend</span>
          )}
        </span>
      </div>

      {/* Search-intent headline */}
      <header>
        <h1 className="font-display text-2xl md:text-[1.75rem] font-semibold leading-tight tracking-tight text-text">
          {topic.search_intent}
        </h1>
      </header>

      {topic.blocked_reason && (
        <div className="flex items-start gap-2 rounded-card border border-warning/30 bg-warning-soft/50 px-3 py-2.5 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <span className="text-text-2">
            <span className="font-semibold text-warning">Blocked: </span>
            {topic.blocked_reason}
          </span>
        </div>
      )}

      {/* Two columns: article (left) + control center & evidence (right). */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 lg:gap-8 items-start">
        {/* LEFT — the article */}
        <section className="min-w-0">
          {editing ? (
            <ArticleEditor
              value={editBody}
              onChange={setEditBody}
              onSave={saveEdit}
              onCancel={() => setEditing(false)}
              saving={saving}
            />
          ) : streaming ? (
            <article className="rounded-2xl border border-cream-300 dark:border-ink-600 bg-cream-50/40 dark:bg-ink-800/40 p-5 md:p-7">
              <div className="mb-4 inline-flex items-center gap-2 text-xs font-medium text-brand-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Writing …
              </div>
              <div className="max-w-[72ch]">
                <Md>{streamText}</Md>
              </div>
            </article>
          ) : article ? (
            <article className="rounded-2xl border border-cream-300 dark:border-ink-600 bg-cream-50/40 dark:bg-ink-800/40 p-5 md:p-7">
              <div className="max-w-[72ch] [&_img]:max-w-full [&_img]:rounded-xl [&_img]:my-3">
                {article.hero_image_url && (
                  <img
                    src={article.hero_image_url}
                    alt={article.title || topic.search_intent}
                    onClick={isImageClickable(article.hero_image_url) ? () => openPanel(article.hero_image_url!) : undefined}
                    className={`w-full max-h-80 object-cover rounded-xl mb-5 border border-cream-200 dark:border-ink-600 ${
                      isImageClickable(article.hero_image_url)
                        ? "cursor-zoom-in transition hover:brightness-95 hover:ring-2 hover:ring-brand-400/50"
                        : ""
                    }`}
                  />
                )}
                <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink mb-4">
                  {article.title || topic.search_intent}
                </h1>
                <Md onImageClick={openPanel} isImageClickable={isImageClickable}>
                  {bodyWithoutHero(article.body_md, article.hero_image_url)}
                </Md>
              </div>
            </article>
          ) : (
            <div className="rounded-2xl border border-dashed border-cream-300 dark:border-ink-600 bg-cream-50/30 dark:bg-ink-800/20 p-10 text-center">
              <Sparkles className="mx-auto mb-3 h-7 w-7 text-brand-500/60" />
              <div className="font-display text-lg font-semibold text-text">Not written yet</div>
              <p className="mx-auto mt-1 max-w-sm text-sm text-text-3">
                {noEvi
                  ? "This topic has no caveat-free evidence yet, so it cannot be grounded."
                  : "Generate the article from the control center on the right — it streams in here."}
              </p>
              {!noEvi && (
                <button
                  onClick={generate}
                  disabled={generating}
                  className="mt-4 inline-flex items-center gap-2 rounded-control bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
                  {generating ? "Generating …" : "Generate article"}
                </button>
              )}
            </div>
          )}
        </section>

        {/* RIGHT — control center (above) + evidence core (below), or the image panel */}
        {panelImage ? (
          <aside className="hidden lg:block sticky top-6 self-start max-h-[calc(100vh-2.5rem)] overflow-y-auto pr-0.5">
            <ImageEditPanel
              key={panelImage.url}
              image={panelImage}
              articleId={article!.id}
              onClose={() => setPanelImage(null)}
              onReplaced={async () => {
                setPanelImage(null);
                if (article) await loadArticle(article.id);
              }}
            />
          </aside>
        ) : (
          <aside className="lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2.5rem)] lg:overflow-y-auto pr-0.5 space-y-5">
            {/* Schaltzentrale */}
            <div className="rounded-2xl border border-cream-300 dark:border-ink-600 bg-cream-50/60 dark:bg-ink-800/50 p-4 space-y-4">
              <RailLabel>Control center</RailLabel>

              {/* Primary action */}
              {generating ? (
                <button
                  onClick={stopGenerate}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-control border border-border px-4 py-2 text-sm font-medium text-text-2 hover:bg-surface-3"
                >
                  <Square className="h-3.5 w-3.5" /> Stop
                </button>
              ) : (
                <button
                  onClick={generate}
                  disabled={noEvi}
                  title={noEvi ? "No caveat-free evidence — cannot ground an article." : undefined}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-control bg-accent-500 px-4 py-2 text-sm font-medium text-white hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <PenLine className="h-4 w-4" />
                  {hasArticle ? "Regenerate" : "Generate article"}
                </button>
              )}

              {/* Secondary actions — need an article */}
              <div className="grid grid-cols-2 gap-2">
                <RailButton onClick={startEdit} disabled={!hasArticle || generating} icon={<Pencil className="h-3.5 w-3.5" />}>
                  Edit
                </RailButton>
                <RailButton onClick={addImages} disabled={!hasArticle || generating || imgBusy} icon={imgBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}>
                  {imgBusy ? "Images …" : "Images"}
                </RailButton>
                <RailButton onClick={() => setHeroOpen(true)} disabled={!hasArticle || generating} icon={<Wand2 className="h-3.5 w-3.5" />}>
                  Hero image
                </RailButton>
                <RailButton onClick={copyMarkdown} disabled={!hasArticle} icon={copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}>
                  {copied ? "Copied" : "Copy MD"}
                </RailButton>
              </div>

              {/* Meta */}
              {article && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-3 text-[0.68rem] text-text-3">
                  <span>{article.claims_count} claims</span>
                  <span>{article.references.length} sources</span>
                  {article.story_count > 0 && <span>{article.story_count} voice</span>}
                  {article.governance_required && (
                    <span className={`inline-flex items-center gap-1 ${article.governance_ok ? "text-emerald-600" : "text-amber-600"}`}>
                      <ShieldAlert className="h-3 w-3" /> gov {article.governance_ok ? "ok" : "backstop"}
                    </span>
                  )}
                  {article.model && (
                    <span className="font-mono">
                      {article.model}
                      {article.cost_eur != null ? ` · €${article.cost_eur.toFixed(3)}` : ""}
                    </span>
                  )}
                </div>
              )}

              {/* Stages */}
              <div className="border-t border-border pt-3">
                <RailLabel sub>Pipeline</RailLabel>
                <div className="mt-2 flex items-center gap-2">
                  <StatusPill status={topic.status} />
                </div>
                <div className="mt-2.5">
                  <PipelineStepper status={topic.status} />
                </div>
              </div>

              {/* Certificates */}
              {topic.cert_chips.length > 0 && (
                <div className="border-t border-border pt-3">
                  <RailLabel sub>Certificates</RailLabel>
                  <div className="mt-2">
                    <CertChips chips={topic.cert_chips} />
                  </div>
                </div>
              )}
            </div>

            {article && !generating && <SeoPanel article={article} onSaved={setArticle} />}

            {/* Trend context (Kür topics): the trend this was promoted from —
                the writer's angle. Collapsible: short preview → full brief. */}
            {trend && <TrendContextCard trend={trend} />}

            {/* Mechanism of action: the MoA cards assigned to this topic (LLM-
                matched). Sits above the raw evidence — mechanism in the
                foreground, footnotes + provenance visible on each card. */}
            {topic.moa_cards && topic.moa_cards.length > 0 && (
              <div>
                <RailLabel>Mechanism of action</RailLabel>
                <p className="mb-2.5 mt-0.5 text-xs text-text-3">
                  How the molecule works — the mechanism behind this benefit.
                </p>
                <div className="space-y-3">
                  {topic.moa_cards.map((c) => (
                    <MoACard key={c.id} card={c} />
                  ))}
                </div>
              </div>
            )}

            {/* Evidence core */}
            <div>
              <RailLabel>Evidence core</RailLabel>
              <p className="mb-2.5 mt-0.5 text-xs text-text-3">
                All caveat-free claims of this rubric, strongest first — the writer's grounding.
              </p>
              {noEvi ? (
                <div className="flex items-start gap-2 rounded-card border border-warning/30 bg-warning-soft/40 px-3 py-2.5 text-sm text-text-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                  No caveat-free evidence yet — this topic cannot be grounded.
                </div>
              ) : (
                <div className="space-y-3">
                  {topic.evidence.map((c) => (
                    <EvidenceCard key={c.id} claim={c} />
                  ))}
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {article && (
        <HeroStudioModal
          articleId={article.id}
          articleTitle={article.title || topic.search_intent}
          open={heroOpen}
          onClose={() => setHeroOpen(false)}
          onAdopted={() => loadArticle(article.id)}
        />
      )}
    </div>
  );
}

function ArticleEditor({
  value,
  onChange,
  onSave,
  onCancel,
  saving,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  return (
    <div className="rounded-2xl border border-brand-400/40 bg-cream-50/40 dark:bg-ink-800/40 p-4 md:p-5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-text">
          <Pencil className="h-4 w-4" /> Edit article
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded-control border border-border px-3 py-1.5 text-sm text-text-2 hover:bg-surface-3 disabled:opacity-40"
          >
            <X className="h-3.5 w-3.5" /> Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded-control bg-accent-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-600 disabled:opacity-40"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </button>
        </div>
      </div>
      <p className="mb-2 text-xs text-text-3">Markdown. Your edit is authoritative — saved verbatim.</p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="min-h-[60vh] w-full rounded-lg border border-cream-300 dark:border-ink-600 bg-white dark:bg-ink-900 p-4 font-mono text-[13px] leading-relaxed text-text focus:outline-none focus:ring-2 focus:ring-brand-400/40"
      />
    </div>
  );
}

function TrendContextCard({ trend }: { trend: TrendContext }) {
  const [open, setOpen] = useState(false);
  const framing = trend.framing_md || "";
  const isLong = framing.length > 170;
  return (
    <div className="rounded-2xl border border-info/30 bg-info-soft/25 p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <RailLabel>Trend</RailLabel>
        <TrendMatchBadge match={trend.claim_match} />
      </div>
      <Link to={`/blog/trend/${trend.id}`} className="group block">
        <span className="inline-flex items-center gap-1.5 font-display text-sm font-semibold text-text group-hover:text-accent-700 dark:group-hover:text-accent-300">
          <TrendingUp className="h-3.5 w-3.5 shrink-0 text-accent-500" /> {trend.term}
        </span>
        {trend.headline && <p className="mt-0.5 text-xs text-text-2">{trend.headline}</p>}
      </Link>
      {framing && (
        <div className="text-[0.82rem] leading-6 text-text-2">
          {open ? (
            <div className="[&_p]:my-1.5">
              <Md>{framing}</Md>
            </div>
          ) : (
            <p className="line-clamp-3 cursor-pointer" onClick={() => isLong && setOpen(true)}>
              {framing}
            </p>
          )}
          {isLong && (
            <button
              onClick={() => setOpen((o) => !o)}
              className="mt-1 text-xs font-medium text-accent-700 hover:text-accent-600 dark:text-accent-300"
            >
              {open ? "Show less" : "… Show more"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function RailButton({
  onClick,
  disabled,
  icon,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-1.5 rounded-control border border-border px-2.5 py-2 text-xs font-medium text-text-2 hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {icon}
      {children}
    </button>
  );
}

function RailLabel({ children, sub }: { children: ReactNode; sub?: boolean }) {
  return (
    <div
      className={`font-display font-semibold uppercase tracking-[0.12em] ${
        sub ? "text-[0.66rem] text-text-3" : "text-[0.72rem] text-text-2"
      }`}
    >
      {children}
    </div>
  );
}

function BackLink() {
  return <BlogBackLink to="/blog/dashboard?tab=backlog" label="Back to Articles" />;
}
